<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { gsap } from "gsap";
import { useGlobalStore } from "@stores/global";

const globalStore = useGlobalStore();

// Clip sources for the floating panels. Prefer the managed intro clips from the
// API; fall back to a static list while they load (or if the request fails) so
// the background is never blank.
const FALLBACK_CLIPS = [
    "https://cloud.papercrowns.com/video/upload/w_256,q_auto:eco/v1/exspeedia/intro/01KW9K4JYKKW3PV052N7MMRD67.mp4",
];

const clips = computed(() =>
    globalStore.introClips.length
        ? globalStore.introClips.map((c) => ({
              src: c.video,
              poster: c.poster,
          }))
        : FALLBACK_CLIPS.map((src) => ({ src, poster: undefined })),
);

// Resolve the clip for a given panel index, wrapping so every clip is used and
// the layout never indexes past the available list.
const clipFor = (i) => clips.value[i % clips.value.length];

// Panel layout (position/size/depth). The clip shown in each panel is resolved
// by panel index via `clipFor`, so every available clip gets surfaced.
const PANELS = [
    // lane A (x ~1–17)
    { x: 1, y: 52, w: 16, depth: 0.55 },
    { x: 3, y: 6, w: 13, depth: 0.3 },
    // lane B (x ~20–44) — single big hero panel
    { x: 20, y: 16, w: 24, depth: 1.0 },
    // lane C (x ~46–61)
    { x: 46, y: 12, w: 15, depth: 0.5 },
    { x: 47, y: 74, w: 14, depth: 0.4 },
    // lane D (x ~64–82) — single panel
    { x: 64, y: 46, w: 18, depth: 0.7 },
    // lane E (x ~84–99)
    { x: 84, y: 4, w: 15, depth: 0.85 },
    { x: 85, y: 64, w: 14, depth: 0.35 },
];

// Fixed panel aspect ratio (width : height). Locks every clip to 16:9.
const ASPECT = 16 / 9;

const SCROLL_SPEED = 77; // auto-float speed of the field
const PERSPECTIVE = 6000; // px — higher = flatter / subtler 3D
const TILT_MAX = 34; // deg of camera rotation at full mouse deflection
const DEPTH_Z = 900; // px Z spread between near and far panels
const MOUSE_SHIFT = 400; // secondary planar parallax, also inverted
const MOUSE_DARK = 0.5; // panels turning back darken, those turning forward brighten
const DEPTH_BLUR = 3; // far panels soften for depth, but stay a quick read
const MAX_DARK = 0.55;
const GLOBAL_DARK = 0.15;
const EDGE_FADE = 0.2; // fraction of the viewport height a panel fades over at the top/bottom edges
const DESKTOP_QUERY = "(min-width: 1025px)";
const MOBILE_SCALE = 2; // panels are scaled up on mobile so the clips read bigger

const stage = ref(null);
const panelEls = ref([]);
const overlayEls = ref([]);
// Drives the per-panel width: bigger clips below the desktop breakpoint.
const isMobile = ref(false);
const panelW = (p) => p.w * (isMobile.value ? MOBILE_SCALE : 1);

// Per-panel at-rest darkness + static depth blur (the rest is driven each frame).
function baseDark(depth) {
    return Math.min(1, (1 - depth) * MAX_DARK + GLOBAL_DARK);
}
function depthBlur(depth) {
    return (1 - depth) * DEPTH_BLUR;
}

let ctx;

onMounted(() => {
    globalStore.loadIntroClips();

    if (!stage.value) return;

    ctx = gsap.context(() => {
        const reduce = gsap.matchMedia
            ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
            : false;

        // Eased camera target — gsap.quickTo glides these toward the raw cursor,
        // which is what gives the camera its inertia. -0.5..0.5 across the viewport.
        const cam = { x: 0, y: 0 };
        const xTo = gsap.quickTo(cam, "x", {
            duration: 1.1,
            ease: "power2.out",
        });
        const yTo = gsap.quickTo(cam, "y", {
            duration: 1.1,
            ease: "power2.out",
        });

        const mq = window.matchMedia(DESKTOP_QUERY);
        let interactive = !reduce && mq.matches;
        isMobile.value = !mq.matches;

        const onMove = (e) => {
            if (!interactive) return;
            xTo(e.clientX / window.innerWidth - 0.5);
            yTo(e.clientY / window.innerHeight - 0.5);
        };
        window.addEventListener("mousemove", onMove);

        const onBp = () => {
            interactive = !reduce && mq.matches;
            isMobile.value = !mq.matches;
            if (!interactive) {
                xTo(0);
                yTo(0);
            }
        };
        mq.addEventListener("change", onBp);

        const setRX = gsap.quickSetter(stage.value, "rotationX", "deg");
        const setRY = gsap.quickSetter(stage.value, "rotationY", "deg");
        const setters = PANELS.map((_, i) => {
            const el = panelEls.value[i];
            const ov = overlayEls.value[i];
            return {
                x: gsap.quickSetter(el, "x", "px"),
                y: gsap.quickSetter(el, "y", "px"),
                z: gsap.quickSetter(el, "z", "px"),
                fade: gsap.quickSetter(el, "opacity"),
                dark: gsap.quickSetter(ov, "opacity"),
            };
        });

        const update = (time) => {
            const t = reduce ? 0 : time;
            const innerH = window.innerHeight;
            const innerW = window.innerWidth;

            // Inverse-camera rotation: the field turns away from the cursor.
            const tilt = interactive ? TILT_MAX : 0;
            const shift = interactive ? MOUSE_SHIFT : 0;
            const ry = -cam.x * tilt;
            const rx = cam.y * tilt;
            setRX(rx);
            setRY(ry);
            // How far the plane has turned — drives the depth shading.
            const sinRy = Math.sin((ry * Math.PI) / 180);
            const sinRx = Math.sin((rx * Math.PI) / 180);

            for (let i = 0; i < PANELS.length; i++) {
                const p = PANELS[i];
                const s = setters[i];
                const w = panelW(p); // vw, scaled up on mobile
                // Height derived from the width to lock the 16:9 box (in px → vh).
                const panelH = ((w / 100) * innerW) / ASPECT;
                const panelHvh = (panelH / innerH) * 100;
                const baseY = (p.y / 100) * innerH;
                const loop = innerH + panelH;
                const drift = t * SCROLL_SPEED * p.depth;
                const top = ((((baseY - drift) % loop) + loop) % loop) - panelH;
                const bob = Math.sin(t * 0.4 + i) * 8 * p.depth;
                // Secondary planar parallax — inverted, scaled by depth so near
                // layers swing further; vertical swing is gentler.
                const px = -cam.x * shift * p.depth;
                const py = -cam.y * shift * p.depth * 0.6;
                // Per-panel depth plane: near panels sit toward the camera.
                const z = (p.depth - 0.5) * DEPTH_Z;
                s.x(bob + px);
                s.y(top + py);
                s.z(z);

                // Fade out near the wrap so panels dissolve at the edges instead
                // of snapping. `top` runs from -panelH (just off the top) to innerH
                // (just off the bottom); fade as it nears either boundary.
                const fadeDist = innerH * EDGE_FADE;
                const edge = Math.min(top + panelH, innerH - top);
                s.fade(Math.max(0, Math.min(1, edge / fadeDist)));

                // Mouse-driven depth shading: panels swinging toward the camera
                // brighten, those swinging back darken (matches the real motion).
                const offX = (p.x + w / 2 - 50) / 50; // -1..1 across viewport
                const offY = (p.y + panelHvh / 2 - 50) / 50;
                const near = Math.min(
                    1,
                    Math.max(
                        0,
                        p.depth + (-offX * sinRy + offY * sinRx) * MOUSE_DARK,
                    ),
                );
                s.dark(baseDark(near));
            }
        };

        gsap.ticker.add(update);

        return () => {
            gsap.ticker.remove(update);
            window.removeEventListener("mousemove", onMove);
            mq.removeEventListener("change", onBp);
        };
    }, stage.value);
});

onUnmounted(() => {
    ctx?.revert();
});
</script>

<template>
    <div
        class="pointer-events-none absolute inset-0 overflow-hidden"
        :style="{
            perspective: `${PERSPECTIVE}px`,
            perspectiveOrigin: '50% 50%',
        }"
        aria-hidden="true"
    >
        <div
            ref="stage"
            class="absolute inset-0 [will-change:transform] [transform-style:preserve-3d]"
        >
            <div
                v-for="(p, i) in PANELS"
                :key="i"
                :ref="(el) => (panelEls[i] = el)"
                class="border-marine-600 bg-marine-700 absolute top-0 left-0 overflow-hidden border-[3px] [will-change:transform]"
                :style="{
                    left: `${p.x}vw`,
                    width: `${panelW(p)}vw`,
                    aspectRatio: '16 / 9',
                }"
            >
                <video
                    :key="clipFor(i).src"
                    :src="clipFor(i).src"
                    :poster="clipFor(i).poster"
                    autoplay
                    loop
                    muted
                    playsinline
                    preload="auto"
                    class="bg-marine-700 absolute inset-0 size-full object-cover"
                    :style="{ filter: `blur(${depthBlur(p.depth)}px)` }"
                ></video>
                <!-- Depth shade — opacity re-driven each frame from the tilt -->
                <div
                    :ref="(el) => (overlayEls[i] = el)"
                    class="bg-marine-800 pointer-events-none absolute inset-0 [will-change:opacity]"
                    :style="{ opacity: baseDark(p.depth) }"
                ></div>
            </div>
        </div>
    </div>
</template>
